import styles from "./ValidateInput.module.scss";
import ShowPassIcon from "./assets/showPassIcon.svg";
import React, { useState, useEffect, type ChangeEvent } from "react";

import { useFormContext } from "react-hook-form";
import clsx from "clsx";
import { formatRuTelInput } from "./formatRuTelInput";

interface ValidateInputProps {
  placeholder: string;
  name: string;
  type?: string;
  required?: boolean;
  pattern?: RegExp;
  nameRu?: string;
  externalError?: string;
  defaultValue?: string | number;
  disabled?: boolean;
  maxLength?: number;
  showGenerateIcon?: boolean;
}

const generatePassword = (length: number = 10): string => {
  const lowercase = "abcdefghijklmnopqrstuvwxyz";
  const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const numbers = "0123456789";
  const symbols = "!@#$%~^&_";
  const allChars = lowercase + uppercase + numbers + symbols;

  let password = "";
  password += lowercase[Math.floor(Math.random() * lowercase.length)];
  password += uppercase[Math.floor(Math.random() * uppercase.length)];
  password += numbers[Math.floor(Math.random() * numbers.length)];
  password += symbols[Math.floor(Math.random() * symbols.length)];

  for (let i = password.length; i < length; i++) {
    password += allChars[Math.floor(Math.random() * allChars.length)];
  }

  return password
    .split("")
    .sort(() => Math.random() - 0.5)
    .join("");
};

export const ValidateInput: React.FC<ValidateInputProps> = ({
  placeholder,
  name,
  type = "text",
  required,
  pattern,
  nameRu,
  externalError,
  defaultValue,
  disabled = false,
  maxLength,
  showGenerateIcon,
}) => {
  const {
    register,
    setValue,
    formState: { errors },
  } = useFormContext();

  const [isShowPass, setIsShowPass] = useState<boolean>(false);
  const isPasswordInput = name === "password" || name === "confirmPassword";

  const {
    onChange: telRhfOnChange,
    onBlur: telOnBlur,
    ref: telRef,
    name: telName,
  } = register(name, { required, pattern });
  const telDefault =
    defaultValue !== undefined && defaultValue !== null && String(defaultValue)
      ? formatRuTelInput(String(defaultValue))
      : "";

  useEffect(() => {
    const handleShowPassword = (event: CustomEvent<boolean>) => {
      setIsShowPass(event.detail);
    };

    document.addEventListener(
      "showPassword" as never,
      handleShowPassword as EventListener,
    );
    return () =>
      document.removeEventListener(
        "showPassword" as never,
        handleShowPassword as EventListener,
      );
  }, []);

  const errorsTextTemplate = (value: string): string => {
    const errorMessages = {
      inn: "Некорректный ИНН",
      kpp: "Некорректный КПП",
      required: "Поле не может быть пустым",
      text: "Введите текст",
      number: "Введите число",
      dealerId: "Выберите дилера",
      role: "Укажите роль",
      email: " ",
      password: " ",
      confirmPassword: "",
      contactPhone: "Некорректный номер",
      shortName: "Поле не должно содержать скобок и не может быть пустым",
    };

    if (Object.hasOwn(errorMessages, value)) {
      return errorMessages[value as keyof typeof errorMessages];
    }

    return "Поле не может быть пустым";
  };

  const onTelChange = (e: ChangeEvent<HTMLInputElement>) => {
    const el = e.target;
    el.value = formatRuTelInput(el.value);
    void telRhfOnChange(e);
  };

  return (
    <div className={styles.inputField}>
      {name === "password" && showGenerateIcon && (
        <p
          className={styles.generatePasswordText}
          onClick={() => {
            const newPassword = generatePassword();
            setValue(name, newPassword, { shouldValidate: true });
            setValue("confirmPassword", newPassword, { shouldValidate: true });
            setIsShowPass(true);
            document.dispatchEvent(
              new CustomEvent("showPassword", { detail: true }),
            );
          }}
        >
          Сгенерировать пароль автоматически
        </p>
      )}
      <span
        className={clsx(styles.inputName, {
          [styles.disabled]: disabled,
          [styles.passFieldName]: name === "password",
        })}
      >
        {nameRu}
      </span>
      <label className={styles.inputLabel}>
        {type === "tel" ? (
          <input
            type="tel"
            inputMode="numeric"
            autoComplete="tel"
            disabled={disabled}
            defaultValue={telDefault}
            placeholder={placeholder}
            className={errors[name] || !!externalError ? styles.errorInput : ""}
            name={telName}
            ref={telRef}
            onBlur={telOnBlur}
            onChange={onTelChange}
          />
        ) : (
          <input
            className={errors[name] || !!externalError ? styles.errorInput : ""}
            type={isPasswordInput && isShowPass ? "text" : type}
            autoComplete={isPasswordInput && isShowPass ? "off" : ""}
            placeholder={placeholder}
            defaultValue={defaultValue}
            {...register(name, { required, pattern })}
            disabled={disabled}
            maxLength={maxLength}
          />
        )}
        {isPasswordInput && (
          <div className={styles.eyeIcon}>
            <ShowPassIcon
              onClick={() => {
                const newState = !isShowPass;
                setIsShowPass(newState);
                document.dispatchEvent(
                  new CustomEvent("showPassword", { detail: newState }),
                );
              }}
            />
          </div>
        )}
      </label>
      {errors && errors[name] && !externalError && (
        <span className={styles.errorInfoText}>{`${errorsTextTemplate(
          name,
        )}`}</span>
      )}
      {!!externalError && (
        <span className={styles.errorInfoText}>{`${externalError}`}</span>
      )}
    </div>
  );
};
